// V21 — Single-path Cash Flow Builder
//
// Architecture (per /app/memory/CF_BUILDER_ARCHITECTURAL_REVIEW.md §4):
//   Read, classify, sum. No derivations. No mapping consulted for math.
//
// Sign conventions
//   - Backend TB/BS returns NATURAL debit-positive signed balances:
//       Asset / Expense (debit-natural)            → positive
//       Liability / Equity / Income (credit-natural)→ negative
//       Accumulated Dep/Amort (contra-asset)       → negative (within asset section)
//   - For ANY non-cash account: presentation_delta = -raw_delta.
//   - Cash-flow effect of a non-cash account delta uses the universal rule
//     `value = -raw_delta`:
//       Asset INCREASE  (raw_delta>0) → value<0 (cash use)         ✓
//       Liab  INCREASE  (raw_delta<0) → value>0 (cash source)     ✓
//       Debt  paydown   (raw_delta>0) → value<0 (cash use)         ✓
//       Equity grew    (raw_delta<0) → value>0 (cash source)      ✓
//
// Equity & the BS NetIncome row (the corner case the architectural review
// underspecified):
//   The QBO BS NetIncome row is reported separately from equity capital
//   accounts. For an arbitrary period, total economic equity delta is:
//      ΔE = Δ(equity_capital_presentation) + (NI_at_period_end - NI_at_period_start)
//   And ΔE = plPeriod.net_income + (C - D). So:
//      (C - D) = Δ(equity_capital_presentation) + (NI_pe - NI_ps) - plPeriod.net_income
//   This collapses to "subtract plPeriod.net_income" only when both
//   NI_ps == 0 AND NI_pe == plPeriod.net_income — i.e., the period
//   starts at FY start and ends within the same FY. For Q4, mid-month
//   spanning year-end, etc., we must include the BS_NI movement.
//   `opts.niAtPeriodEnd` and `opts.niAtPeriodStart` carry these two
//   anchor values (both from QBO BS at the respective dates).
//
// Inputs
//   accounts  — same shape as V20 buildCashFlow: each account has
//               { id, name, qbo_type, classification, balance,
//                 period_start_balance, is_sub_account }
//   plPeriod  — { net_income, depreciation, amortization, interest_expense,
//                 tax_expense } from QBO ProfitAndLoss for the period.
//   opts      — { beginCash, endCash, materialityThreshold,
//                 niAtPeriodStart, niAtPeriodEnd }.
//
// Output shape mirrors V20 so the renderer can swap.

const ROUND_NOISE = 0.005; // sub-cent — below this is ignored
const round0 = (v) => Math.round(v || 0);

// ───────────────────────────────────────────────────────────────────────────
// Cash identification — QBO classifies natively, no regex needed.
// Note: keep `Undeposited Funds` (qbo_type='Other Current Asset', name match)
// inside cash for parity with QBO's own SCF.
// ───────────────────────────────────────────────────────────────────────────
function isCashAccount(a) {
  const t = (a.qbo_type || '').toLowerCase();
  if (t === 'bank') return true;
  if (t === 'other current asset' && /undeposited/i.test(a.name || '')) return true;
  return false;
}

function isAccumulatedContra(a) {
  // Accumulated Depreciation / Amortization. Detection via qbo_sub_type when
  // populated by ingestion; otherwise via the contra naming convention.
  const n = (a.name || '').toLowerCase();
  if (/accumulated\s+(depreciat|amortiz)/.test(n)) return true;
  if (/\baccum(\.|ulated)?\s+(depr|amort)/.test(n)) return true;
  if (/\bless\s+accum/.test(n)) return true;
  return false;
}

// Classify a non-cash BS account into Op / Inv / Fin using QBO-native fields.
// V21 classify() — primary classification by (qbo_type, qbo_sub_type, name).
// Returns { section: 'op_asset'|'op_liab'|'inv_asset'|'fin_debt'|'fin_equity'
//                    |'accum_contra'|'ni_row'|'other_activity'|null,
//           debtFlag: bool,
//           fallback: bool (true when default-routing kicked in) }.
//
// Hardening (2026-06-01, post-Ashley-Williams meta-pattern review):
// Builders never silently drop accounts. If the primary type/sub_type
// dispatch doesn't match a specific rule, classify() falls back to
// AccountType-family default routing so the CF identity closes by
// construction regardless of whether the classifier has enumerated
// the exact QBO sub-type. The unclassified[] advisory still fires —
// staff sees "new account shape, default-routed to <section>" — but
// the math never breaks.
function classify(a) {
  if (isCashAccount(a)) return { section: null };
  const t = (a.qbo_type || '').toLowerCase();
  const sub = (a.qbo_sub_type || '').toLowerCase();
  const cls = (a.classification || '').toLowerCase();
  const name = (a.name || '').toLowerCase();

  // Accumulated Dep/Amort — captured via Op D&A row from plPeriod.
  if (isAccumulatedContra(a)) return { section: 'accum_contra' };

  // QBO's "Net Income" row on the equity section is a calculated field, not
  // a real account. It does not contribute to the CF (the actual flow is the
  // NI line in operating).
  if (cls === 'equity' && /^net\s+income/i.test(name)) return { section: 'ni_row' };

  // Operating working capital
  if (t === 'accounts receivable') return { section: 'op_asset' };
  if (t === 'inventory' || t === 'other current asset') return { section: 'op_asset' };
  if (t === 'accounts payable') return { section: 'op_liab' };
  if (t === 'credit card') return { section: 'op_liab' };
  if (t === 'other current liability') {
    // Short-term debt classification — QBO sub-types ShortTermDebit or
    // LineOfCredit move to Financing; everything else (accruals, payroll
    // liabilities, deferred revenue) stays in Operating.
    if (/loan|notes? payable|line of credit|short.?term debt/.test(sub + ' ' + name)) {
      return { section: 'fin_debt' };
    }
    return { section: 'op_liab' };
  }

  // Investing — long-term assets (Fixed + Other Asset).
  if (t === 'fixed asset' || t === 'other asset') return { section: 'inv_asset' };

  // Financing — long-term debt + equity.
  if (t === 'long term liability') return { section: 'fin_debt' };
  if (t === 'equity' || cls === 'equity') return { section: 'fin_equity' };

  // ─── Default-routing fallback (V21 hardening, 2026-06-01) ───────────
  // No specific rule matched. If the account is on the BS (classification
  // = Asset/Liability/Equity), route by family so the identity closes:
  //   Asset     → Investing (it's long-term-ish if we got here)
  //   Liability → Operating (most catch-all liabilities are accruals)
  //   Equity    → Financing
  //   anything else → 'other_activity' (catch-all section)
  // Always set fallback:true so the unclassified[] advisory fires.
  if (cls === 'asset')     return { section: 'inv_asset',  fallback: true };
  if (cls === 'liability') return { section: 'op_liab',    fallback: true };
  if (cls === 'equity')    return { section: 'fin_equity', fallback: true };

  // Truly unknown classification — route to "Cash Flows from Other
  // Activities" section. This catches QBO account shapes whose
  // classification field isn't populated or uses a non-standard string.
  return { section: 'other_activity', fallback: true };
}

// ───────────────────────────────────────────────────────────────────────────
// Main builder
// ───────────────────────────────────────────────────────────────────────────
export function buildCashFlowV21(accounts, plPeriod, opts = {}) {
  if (!Array.isArray(accounts)) throw new Error('buildCashFlowV21: accounts must be an array');
  if (!plPeriod || typeof plPeriod !== 'object') {
    throw new Error('buildCashFlowV21: plPeriod required ({net_income, depreciation, amortization, ...})');
  }

  const materialityThreshold = Number(opts.materialityThreshold) > 0
    ? Number(opts.materialityThreshold) : 0;

  // ─── 1. Compute cash totals ─────────────────────────────────────────
  // Cash MUST come from the same per-account `period_start_balance` /
  // `balance` source as every other BS account, otherwise the identity
  // can drift by reconciliation rounding/snapshot differences (the backend
  // exposes `opts.cash_at_period_start` via a separate fetch path that
  // does not always match the sum of Bank account period_start_balances).
  // `opts.beginCash` and `opts.endCash` are still surfaced in the result
  // as `beginCashSnapshot` / `endCashSnapshot` for diagnostic comparison.
  const cashAccounts = accounts.filter(isCashAccount);
  const beginCash = cashAccounts.reduce((s, a) => s + (Number(a.period_start_balance) || 0), 0);
  const endCash   = cashAccounts.reduce((s, a) => s + (Number(a.balance) || 0), 0);
  const beginCashSnapshot = Number.isFinite(opts.beginCash) ? Number(opts.beginCash) : null;
  const endCashSnapshot   = Number.isFinite(opts.endCash)   ? Number(opts.endCash)   : null;
  const cashSnapshotDelta = beginCashSnapshot != null
    ? (beginCashSnapshot - beginCash) : null;  // for advisory
  const deltaCash = endCash - beginCash;

  // ─── 2. Per-account deltas + classification ─────────────────────────
  // Skip sub-accounts whose balance is rolled up into parent (V20 convention).
  // Actually V20 sums leaf accounts; we mirror that — every account contributes
  // its OWN balance, parents do not include sub-account balances. The QBO
  // accounts payload from /api/qbo/accounts returns per-leaf balances.
  //
  // Hardening (2026-06-01): classify() now always returns a section for
  // any BS account. The `fallback:true` flag indicates default-routing
  // (qbo_type didn't match a specific rule). Fallback accounts are still
  // added to the appropriate section (math closes) AND surfaced in the
  // `unclassified` advisory array so staff sees the new account shape.
  // Only I/S accounts (Income / Expense / Cost of Goods Sold) end up in
  // `c.section === null` and are properly skipped (NI flow handles them).
  const rows = [];
  const unclassified = [];
  let accumContraRawDelta = 0;  // sum of AccumDep/AccumAmort raw deltas (negative when growing)
  for (const a of accounts) {
    const delta = (Number(a.balance) || 0) - (Number(a.period_start_balance) || 0);
    if (isCashAccount(a)) continue;
    // Skip I/S accounts (their economic effect flows through plPeriod.net_income).
    const isISAccount = (a.type === 'Income' || a.type === 'Expense'
                      || a.type === 'Cost of Goods Sold'
                      || a.classification === 'Revenue'
                      || a.classification === 'Expense');
    if (isISAccount) continue;

    const c = classify(a);
    if (c.section === 'accum_contra') {
      accumContraRawDelta += delta;
      continue;
    }
    if (c.section === null || c.section === 'ni_row') continue;

    // If classify() default-routed this account, log the advisory so
    // staff can extend the primary classifier for future renders. The
    // math still closes via the default route.
    if (c.fallback) {
      unclassified.push({
        id: a.id, name: a.name, qbo_type: a.qbo_type,
        qbo_sub_type: a.qbo_sub_type, classification: a.classification,
        delta, default_routed_to: c.section,
      });
    }

    rows.push({ ...c, account: a, delta });
  }

  // ─── 3. Emit Operating section ──────────────────────────────────────
  const operating = [];
  operating.push({ label: 'Net Income', value: Number(plPeriod.net_income) || 0, kind: 'subheader-first' });

  // D&A add-back — Option 2 per architectural review §4: the AccumDep BS
  // delta is the ground truth. AccumDep is credit-natural in an asset
  // section, so raw_delta is NEGATIVE when AccumDep grows (more depreciation
  // accumulated). The presentation D&A value is the absolute increase.
  //
  // We also compute the IS-reported D&A (Option 1) and surface a data-
  // quality advisory if the two diverge by >$10 — this is the canary for
  // bookkeeping anomalies like asset write-offs that bypass AccumDep
  // (e.g., the Fayetteville Section 179 Carryover case where the asset
  // was depreciated straight through expense, skipping AccumDep).
  const dAndA_IS = (Number(plPeriod.depreciation) || 0) + (Number(plPeriod.amortization) || 0);
  const dAndA_BS = -accumContraRawDelta;  // positive when AccumDep grew
  const dAndA = dAndA_BS;
  const dAndA_variance = dAndA_IS - dAndA_BS;
  if (Math.abs(dAndA) > ROUND_NOISE) {
    operating.push({ label: 'Depreciation and amortization', value: dAndA, kind: 'adjustment' });
  }

  // Working-capital changes — one row per non-cash op asset/liab.
  // Universal sign rule for non-cash accounts: value = -raw_delta.
  // (Raw deltas are debit-positive natural; flipping gives the correct
  // cash-flow direction for both assets and liabilities — see header.)
  const opMaterial = [];
  let opAssetOther = 0, opLiabOther = 0;
  for (const r of rows) {
    if (r.section === 'op_asset') {
      const value = -r.delta;  // asset increase = use of cash (−)
      if (Math.abs(value) >= materialityThreshold) {
        opMaterial.push({ label: `(Increase) Decrease in ${r.account.name}`, value, kind: 'wc', account_id: r.account.id });
      } else if (Math.abs(value) > ROUND_NOISE) {
        opAssetOther += value;
      }
    } else if (r.section === 'op_liab') {
      const value = -r.delta;  // liab grew (raw <0) → value>0 (source of cash)
      if (Math.abs(value) >= materialityThreshold) {
        opMaterial.push({ label: `Increase (Decrease) in ${r.account.name}`, value, kind: 'wc', account_id: r.account.id });
      } else if (Math.abs(value) > ROUND_NOISE) {
        opLiabOther += value;
      }
    }
  }
  // Sort working-capital lines by descending |value| for CPA presentation.
  opMaterial.sort((x, y) => Math.abs(y.value) - Math.abs(x.value));
  operating.push(...opMaterial);
  if (Math.abs(opAssetOther) > ROUND_NOISE) {
    operating.push({ label: 'Other changes in operating assets', value: opAssetOther, kind: 'rollup' });
  }
  if (Math.abs(opLiabOther) > ROUND_NOISE) {
    operating.push({ label: 'Other changes in operating liabilities', value: opLiabOther, kind: 'rollup' });
  }

  // ─── 4. Emit Investing section ──────────────────────────────────────
  const investing = [];
  let invOther = 0;
  for (const r of rows) {
    if (r.section !== 'inv_asset') continue;
    const value = -r.delta;  // long-term asset increase = use of cash (−)
    if (Math.abs(value) >= materialityThreshold) {
      investing.push({ label: `Acquisitions (Disposals) of ${r.account.name}`, value, kind: 'inv', account_id: r.account.id });
    } else if (Math.abs(value) > ROUND_NOISE) {
      invOther += value;
    }
  }
  investing.sort((x, y) => Math.abs(y.value) - Math.abs(x.value));
  if (Math.abs(invOther) > ROUND_NOISE) {
    investing.push({ label: 'Other investing activities', value: invOther, kind: 'rollup' });
  }

  // ─── 5. Emit Financing section ──────────────────────────────────────
  const financing = [];
  let finOther = 0;

  // Aggregate ALL equity capital deltas (raw) and emit them as a SINGLE
  // financing equity line representing the period's net contributions /
  // distributions. This avoids per-account NI back-out complexity and
  // matches CPA presentation (Contributions and Distributions are normally
  // shown net on the face of the SCF, not per equity account).
  const niAtPeriodEnd   = Number.isFinite(opts.niAtPeriodEnd)   ? Number(opts.niAtPeriodEnd)
                       : Number(plPeriod.net_income) || 0;
  const niAtPeriodStart = Number.isFinite(opts.niAtPeriodStart) ? Number(opts.niAtPeriodStart) : 0;

  const equityRows = rows.filter(r => r.section === 'fin_equity');
  const equityRawDeltaTotal = equityRows.reduce((s, r) => s + (Number(r.delta) || 0), 0);
  // Total equity ΔE (presentation) = -Δ(equity_capital_raw) + Δ(BS_NI presentation)
  //                                = -equityRawDeltaTotal + (niAtPeriodEnd - niAtPeriodStart)
  // (C - D) = ΔE - plPeriod.net_income.
  const totalEquityPresentationDelta = -equityRawDeltaTotal + (niAtPeriodEnd - niAtPeriodStart);
  const equityCashEffect = totalEquityPresentationDelta - (Number(plPeriod.net_income) || 0);

  // Pick a label: largest equity account by |period_start_balance| is the
  // primary; if none qualifies, use a generic label.
  let primaryEquityLabel = 'Owner capital';
  let primaryEquityId = null;
  if (equityRows.length) {
    const best = equityRows.reduce((b, r) => {
      const abs = Math.abs(Number(r.account.period_start_balance) || 0);
      return abs > b.abs ? { id: r.account.id, name: r.account.name, abs } : b;
    }, { id: null, name: null, abs: -1 });
    if (best.id) {
      primaryEquityId = best.id;
      primaryEquityLabel = best.name;
    }
  }

  for (const r of rows) {
    if (r.section === 'fin_debt') {
      const value = -r.delta;  // debt paydown (raw>0) → value<0 (cash use)
      if (Math.abs(value) >= materialityThreshold) {
        financing.push({ label: `Proceeds from (Payments on) ${r.account.name}`, value, kind: 'fin', account_id: r.account.id });
      } else if (Math.abs(value) > ROUND_NOISE) {
        finOther += value;
      }
    }
  }
  // Single combined equity line (after debt rows so it lands last).
  if (equityRows.length && Math.abs(equityCashEffect) > ROUND_NOISE) {
    financing.push({
      label: `Contributions (Distributions) — ${primaryEquityLabel}`,
      value: equityCashEffect,
      kind: 'fin',
    });
  }
  financing.sort((x, y) => Math.abs(y.value) - Math.abs(x.value));
  if (Math.abs(finOther) > ROUND_NOISE) {
    financing.push({ label: 'Other financing activities', value: finOther, kind: 'rollup' });
  }

  // ─── 5b. Emit "Cash Flows from Other Activities" — V21 hardening ─────
  // Catch-all section for accounts that classify() default-routed to
  // 'other_activity' (classification field empty / non-standard). Each
  // row uses the universal sign rule: value = -raw_delta. The presence
  // of this section is itself a quality signal — staff should review
  // why an account lacks classification, but the CF identity ties
  // either way.
  const otherActivities = [];
  let otherActMaterial = 0, otherActImmaterial = 0;
  for (const r of rows) {
    if (r.section !== 'other_activity') continue;
    const value = -r.delta;
    if (Math.abs(value) >= materialityThreshold) {
      otherActivities.push({
        label: `Change in ${r.account.name}`,
        value, kind: 'other', account_id: r.account.id,
      });
      otherActMaterial += value;
    } else if (Math.abs(value) > ROUND_NOISE) {
      otherActImmaterial += value;
    }
  }
  otherActivities.sort((x, y) => Math.abs(y.value) - Math.abs(x.value));
  if (Math.abs(otherActImmaterial) > ROUND_NOISE) {
    otherActivities.push({ label: 'Other unclassified activities', value: otherActImmaterial, kind: 'rollup' });
  }

  // ─── 6. Subtotals & identity check ─────────────────────────────────
  const sumOp  = operating.reduce((s, r) => s + (r.value || 0), 0);
  const sumInv = investing.reduce((s, r) => s + (r.value || 0), 0);
  const sumFin = financing.reduce((s, r) => s + (r.value || 0), 0);
  const sumOther = otherActivities.reduce((s, r) => s + (r.value || 0), 0);
  const sectionsNet = sumOp + sumInv + sumFin + sumOther;
  const identityDelta = sectionsNet - deltaCash;
  // identityDelta MUST be ~0 by construction. Default-routing ensures
  // every non-cash BS delta lands in some section. A non-zero identityDelta
  // now indicates a numerical bug (sign error, double-count) — never a
  // routing gap.

  // ─── 7. Supplemental disclosures (ASC 230) ─────────────────────────
  const supplemental = {
    interestPaid: Number(plPeriod.interest_expense) || 0,
    taxesPaid:    Number(plPeriod.tax_expense) || 0,
  };

  return {
    builder: 'v21',
    netIncome: Number(plPeriod.net_income) || 0,
    dAndA,
    dAndA_IS,
    dAndA_BS,
    dAndA_variance,  // advisory: should be ~0 unless bookkeeping anomaly
    sections: {
      operating: { rows: operating, subtotal: sumOp,
                   label: 'Net Cash from Operating Activities' },
      investing: { rows: investing, subtotal: sumInv,
                   label: 'Net Cash from Investing Activities' },
      financing: { rows: financing, subtotal: sumFin,
                   label: 'Net Cash from Financing Activities' },
      // Optional fourth section — only present (non-empty rows) when at
      // least one BS account default-routed to other_activity. Most
      // engagements render with just the standard three sections.
      other: { rows: otherActivities, subtotal: sumOther,
               label: 'Net Cash from Other Activities' },
    },
    beginCash, endCash, netChange: deltaCash,
    beginCashSnapshot, endCashSnapshot, cashSnapshotDelta,
    sectionsNet,
    identityDelta,
    identityPasses: Math.abs(identityDelta) < 1.00,  // dollar-level tolerance
    supplemental,
    unclassified,
    diagnostic: {
      cash_accounts: cashAccounts.length,
      non_cash_accounts_classified: rows.length,
      unclassified_count: unclassified.length,
      primary_equity_id: primaryEquityId,
    },
  };
}

// ───────────────────────────────────────────────────────────────────────────
// Divergence comparator — used by side-by-side Day 2 logging.
// Returns an array of finding records: each item describes one $1+ delta
// between V20 and V21 outputs.
// ───────────────────────────────────────────────────────────────────────────
export function diffCashFlows(v20, v21, ctx = {}) {
  const findings = [];
  const tol = 1.00;  // sub-dollar is noise

  const push = (field, v20val, v21val) => {
    const a = Number(v20val) || 0;
    const b = Number(v21val) || 0;
    if (Math.abs(a - b) >= tol) {
      findings.push({
        ...ctx,
        field, v20: a, v21: b, delta: b - a,
      });
    }
  };

  push('net_income', v20?.netIncome, v21?.netIncome);
  push('d_and_a',    v20?.dAndA,     v21?.dAndA);
  push('operating_subtotal', v20?.sections?.operating?.subtotal, v21?.sections?.operating?.subtotal);
  push('investing_subtotal', v20?.sections?.investing?.subtotal, v21?.sections?.investing?.subtotal);
  push('financing_subtotal', v20?.sections?.financing?.subtotal, v21?.sections?.financing?.subtotal);
  push('begin_cash',  v20?.beginCash, v21?.beginCash);
  push('end_cash',    v20?.endCash,   v21?.endCash);
  push('net_change',  v20?.netChange, v21?.netChange);
  push('identity_delta', v20?.identityDelta ?? null, v21?.identityDelta ?? null);
  // V20's identity (built-in `cashFlowTiesPassing`) — call out when V21 lifts
  // it from red → green.
  const v20Identity = (v20?.netChange ?? 0)
    - ((v20?.sections?.operating?.subtotal || 0)
       + (v20?.sections?.investing?.subtotal || 0)
       + (v20?.sections?.financing?.subtotal || 0));
  if (Math.abs(v20Identity) >= tol) {
    findings.push({
      ...ctx,
      field: '!V20_identity_breaks',
      v20: v20Identity,
      v21: v21?.identityDelta ?? 0,
      delta: (v21?.identityDelta ?? 0) - v20Identity,
      note: `V20 identity ${round0(v20Identity)} ≠ 0; V21 ${round0(v21?.identityDelta ?? 0)}`,
    });
  }
  return findings;
}

export const _V21_INTERNALS = { isCashAccount, isAccumulatedContra, classify };
